import type { Maybe } from "@entrilia/shared/types.ts";
import type { ConditionConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import { getRelativeDateResolvedString, getValidatedDateString } from "../../../../../utils/dateUtils.ts";
import { isRelativePeriodValue } from "../../../../../utils/relativeDates.ts";
import ConditionDimensionCaption from "./ConditionDimensionCaption.tsx";
import ConditionValuesTooltip from "./ConditionValuesTooltip.tsx";
import FieldCaptionWrapper from "./FieldCaptionWrapper.tsx";

export const formatCaption = (
  field: ConditionField,
  color?: string,
  hideEqualitySignForSingleDate?: boolean,
  captionVariant?: "caption" | "body1"
) => {
  const customCaption = getCustomCaption(field, hideEqualitySignForSingleDate);
  const isDate = field.meta.type === ItemDataType.Date;
  const subCaptionTooltip = isDate
    ? getRelativeDateResolvedString(field.config.filter?.values?.[0], field.meta.defaultFormat?.formatType)
    : undefined;
  const values = field.config.filter?.values?.map(getFormattedValue) ?? [];
  return (
    <FieldCaptionWrapper
      Caption={<ConditionDimensionCaption field={field} captionColor={color} captionVariant={captionVariant} />}
      subCaptionPrefix={customCaption?.prefix}
      subCaption={customCaption?.value || ""}
      subCaptionColor={color}
      subCaptionTooltip={subCaptionTooltip}
      subCaptionValuesTooltip={isDate || values.length === 0 ? undefined : <ConditionValuesTooltip values={values} />}
      isMandatory={field.config.mandatory}
    />
  );
};

export interface ConditionCaption {
  prefix: string;
  value: string;
}

export const getCustomCaption = (
  field: ConditionField,
  hideEqualitySignForSingleDate?: boolean
): ConditionCaption | undefined => {
  const params = field.config.filter?.values?.length || 0;
  const value = field.config.filter?.values.map(getFormattedValue);
  if (field.meta.type === ItemDataType.Date) {
    if (params === 1) {
      const label = getValidatedDateString(value[0], field.meta.defaultFormat?.formatType);
      if (isRelativePeriodValue(field.config.filter?.values[0])) {
        return { prefix: "", value: label };
      }
      const equalitySign = hideEqualitySignForSingleDate ? "" : getEqualitySign(field.config.filter.equalityType);
      return { prefix: equalitySign, value: label };
    }
    if (params === 2) {
      const from = getValidatedDateString(value[0], field.meta.defaultFormat?.formatType);
      const to = getValidatedDateString(value[1], field.meta.defaultFormat?.formatType);
      return { prefix: "", value: `${from}..${to}` };
    }
    return undefined;
  }

  if (params === 0) {
    return undefined;
  }

  return { prefix: getEqualityPrefix(field.config.filter?.equalityType), value: value.join(", ") };
};

export const getFormattedValue = (value: Maybe<string>): string => {
  return value ?? "No Value";
};

const getEqualityPrefix = (equalityType?: ConditionEqualityType) => {
  switch (equalityType) {
    case ConditionEqualityType.Equal:
      return "is";
    case ConditionEqualityType.NotEqual:
      return "is not";
    case ConditionEqualityType.Contains:
      return "contains";
    case ConditionEqualityType.DoesntContain:
      return "doesn't contain";
    case ConditionEqualityType.Between:
      return "between";
    case ConditionEqualityType.LessThan:
    case ConditionEqualityType.LessThanOrEqual:
    case ConditionEqualityType.GreaterThan:
    case ConditionEqualityType.GreaterThanOrEqual:
      return getEqualitySign(equalityType);
    default:
      return "";
  }
};

const getEqualitySign = (equalityType?: ConditionEqualityType) => {
  if (equalityType === undefined) return "";
  switch (equalityType) {
    case ConditionEqualityType.Equal:
      return "=";
    case ConditionEqualityType.NotEqual:
      return "<>";
    case ConditionEqualityType.LessThan:
      return "<";
    case ConditionEqualityType.LessThanOrEqual:
      return "≤";
    case ConditionEqualityType.GreaterThan:
      return ">";
    case ConditionEqualityType.GreaterThanOrEqual:
      return "≥";
    default:
      return "";
  }
};

export const resetMandatoryField = (
  fieldConfiguration: ConditionConfiguration,
  changes: Partial<ConditionConfiguration>
) => {
  if (changes.parameter === false && fieldConfiguration.mandatory === true) {
    changes.mandatory = false;
  }
  return changes;
};
